import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  DeleteRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@mui/icons-material';
import type { FeaturedEvent } from '@dosfilos/domain';
import { formatDateOnly } from '@dosfilos/shared';
import { TenantDateField } from '../../../components/TenantDateField';
import { useTenantDateFormat } from '../../../stores/tenant.store';
import { LandingImageField } from './LandingImageField';
import {
  MAX_FEATURED_EVENTS,
  eventVisibility,
  newFeaturedEvent,
  validateFeaturedEvent,
  type EventVisibility,
  type FeaturedEventError,
} from '../../../lib/featured-events';

const STATUS_COLOR: Record<EventVisibility, 'success' | 'info' | 'default' | 'warning'> = {
  visible: 'success',
  scheduled: 'info',
  hidden: 'default',
  ended: 'default',
  invalid: 'warning',
};

/**
 * Editor de los eventos destacados del landing (Configuración → Landing).
 * Cada fila dice si el evento se ve HOY y, si no, por qué: un evento que no
 * aparece sin explicación es lo primero que el director va a reportar.
 */
export function FeaturedEventsEditor({
  tenantId,
  events,
  today,
  onChange,
}: {
  tenantId: string;
  events: FeaturedEvent[];
  /** `YYYY-MM-DD` en la zona del seminario. */
  today: string;
  onChange: (next: FeaturedEvent[]) => void;
}) {
  const { t } = useTranslation('sis');
  const dateFormat = useTenantDateFormat();
  const [openId, setOpenId] = useState<string | null>(null);

  const patch = (id: string, change: Partial<FeaturedEvent>) =>
    onChange(events.map((e) => (e.id === id ? ({ ...e, ...change } as FeaturedEvent) : e)));

  const add = () => {
    const ev = newFeaturedEvent(crypto.randomUUID());
    onChange([...events, ev]);
    setOpenId(ev.id);
  };

  const incomplete = events.some((e) => validateFeaturedEvent(e).length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {t('settings.landing.featuredEvents.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('settings.landing.featuredEvents.description')}
      </Typography>

      <Stack spacing={1.5}>
        {events.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('settings.landing.featuredEvents.empty')}
          </Typography>
        )}

        {incomplete && (
          <Alert severity="warning">{t('settings.landing.featuredEvents.incomplete')}</Alert>
        )}

        {events.map((ev) => {
          const open = openId === ev.id;
          const errors = validateFeaturedEvent(ev);
          const status = eventVisibility(ev, today);
          const statusLabel =
            status === 'scheduled' && ev.visibleFrom
              ? t('settings.landing.featuredEvents.status.scheduled', {
                  date: formatDateOnly(ev.visibleFrom, dateFormat),
                })
              : t(`settings.landing.featuredEvents.status.${status}`);
          const errorText = (field: FeaturedEventError) =>
            errors.includes(field)
              ? t(`settings.landing.featuredEvents.errors.${field}`)
              : undefined;

          return (
            <Paper key={ev.id} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {ev.title.trim() || t('settings.landing.featuredEvents.untitled')}
                  </Typography>
                  {ev.startDate && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {formatDateOnly(ev.startDate, dateFormat)}
                      {ev.endDate && ev.endDate !== ev.startDate
                        ? ` – ${formatDateOnly(ev.endDate, dateFormat)}`
                        : ''}
                      {ev.venue.trim() ? ` · ${ev.venue.trim()}` : ''}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  color={STATUS_COLOR[status]}
                  variant="outlined"
                  label={statusLabel}
                />
                <Tooltip
                  title={
                    open
                      ? t('settings.landing.featuredEvents.collapse')
                      : t('settings.landing.featuredEvents.expand')
                  }
                >
                  <IconButton
                    size="small"
                    aria-expanded={open}
                    aria-label={
                      open
                        ? t('settings.landing.featuredEvents.collapse')
                        : t('settings.landing.featuredEvents.expand')
                    }
                    onClick={() => setOpenId(open ? null : ev.id)}
                  >
                    {open ? (
                      <ExpandLessRounded fontSize="small" />
                    ) : (
                      <ExpandMoreRounded fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('settings.landing.featuredEvents.remove')}>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('settings.landing.featuredEvents.remove')}
                    onClick={() => onChange(events.filter((e) => e.id !== ev.id))}
                  >
                    <DeleteRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Collapse in={open} unmountOnExit>
                <Stack spacing={2} sx={{ p: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ev.active}
                        onChange={(e) => patch(ev.id, { active: e.target.checked })}
                      />
                    }
                    label={t('settings.landing.featuredEvents.activeLabel')}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label={t('settings.landing.featuredEvents.labelLabel')}
                      placeholder={t('settings.landing.featuredEvents.labelPlaceholder')}
                      value={ev.label}
                      onChange={(e) => patch(ev.id, { label: e.target.value })}
                      size="small"
                      sx={{ minWidth: { sm: 200 } }}
                    />
                    <TextField
                      label={t('settings.landing.featuredEvents.titleLabel')}
                      placeholder={t('settings.landing.featuredEvents.titlePlaceholder')}
                      value={ev.title}
                      onChange={(e) => patch(ev.id, { title: e.target.value })}
                      size="small"
                      fullWidth
                      required
                      error={!!errorText('title')}
                      helperText={errorText('title')}
                    />
                  </Stack>
                  <TextField
                    label={t('settings.landing.featuredEvents.descriptionLabel')}
                    value={ev.description ?? ''}
                    onChange={(e) => patch(ev.id, { description: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TenantDateField
                      label={t('settings.landing.featuredEvents.startDateLabel')}
                      value={ev.startDate}
                      onChange={(v) => patch(ev.id, { startDate: v })}
                      fullWidth
                      error={!!errorText('startDate')}
                      helperText={errorText('startDate') ?? ''}
                    />
                    <TenantDateField
                      label={t('settings.landing.featuredEvents.endDateLabel')}
                      value={ev.endDate ?? ''}
                      onChange={(v) => patch(ev.id, { endDate: v })}
                      fullWidth
                      {...(ev.startDate ? { min: ev.startDate } : {})}
                      error={!!errorText('endDate')}
                      helperText={
                        errorText('endDate') ?? t('settings.landing.featuredEvents.endDateHelper')
                      }
                    />
                    <TenantDateField
                      label={t('settings.landing.featuredEvents.visibleFromLabel')}
                      value={ev.visibleFrom ?? ''}
                      onChange={(v) => patch(ev.id, { visibleFrom: v })}
                      fullWidth
                      helperText={t('settings.landing.featuredEvents.visibleFromHelper')}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label={t('settings.landing.featuredEvents.venueLabel')}
                      placeholder={t('settings.landing.featuredEvents.venuePlaceholder')}
                      value={ev.venue}
                      onChange={(e) => patch(ev.id, { venue: e.target.value })}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t('settings.landing.featuredEvents.priceLabel')}
                      value={ev.price ?? ''}
                      onChange={(e) => patch(ev.id, { price: e.target.value })}
                      size="small"
                      helperText={t('settings.landing.featuredEvents.priceHelper')}
                      sx={{ minWidth: { sm: 220 } }}
                    />
                  </Stack>
                  <TextField
                    label={t('settings.landing.featuredEvents.urlLabel')}
                    placeholder="https://"
                    value={ev.registrationUrl}
                    onChange={(e) => patch(ev.id, { registrationUrl: e.target.value })}
                    size="small"
                    fullWidth
                    required
                    type="url"
                    error={!!errorText('registrationUrl')}
                    helperText={
                      errorText('registrationUrl') ?? t('settings.landing.featuredEvents.urlHelper')
                    }
                  />
                  <LandingImageField
                    tenantId={tenantId}
                    fileStem={`featured-event-${ev.id}`}
                    value={ev.imageUrl ?? ''}
                    onChange={(imageUrl) => patch(ev.id, { imageUrl })}
                    i18nPrefix="settings.landing.featuredEvents.image"
                    aspectRatio="2 / 1"
                    previewWidth={280}
                    fade
                  />
                </Stack>
              </Collapse>
            </Paper>
          );
        })}

        <Box>
          {events.length < MAX_FEATURED_EVENTS ? (
            <Button variant="outlined" size="small" startIcon={<AddRounded />} onClick={add}>
              {t('settings.landing.featuredEvents.add')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('settings.landing.featuredEvents.limit', { max: MAX_FEATURED_EVENTS })}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
